import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Select, DatePicker, Space, Tag, Modal, message, Image, Popconfirm } from 'antd';
import { SearchOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import { getPostList, getPostDetail, deletePost, batchDeletePosts } from '../api/posts';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;

function Posts() {
  const [loading, setLoading] = useState(false);
  const [posts, setPosts] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({
    keyword: '',
    status: '',
    dateRange: null
  });

  // 详情弹窗
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentPost, setCurrentPost] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // 批量选择
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [batchDeleteLoading, setBatchDeleteLoading] = useState(false);

  // 加载数据
  const loadData = async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page,
        pageSize: pagination.pageSize,
        keyword: filters.keyword || undefined,
        status: filters.status || undefined,
        startDate: filters.dateRange ? filters.dateRange[0].format('YYYY-MM-DD') : undefined,
        endDate: filters.dateRange ? filters.dateRange[1].format('YYYY-MM-DD') : undefined
      };

      const res = await getPostList(params);
      if (res.success) {
        setPosts(res.data.list);
        setPagination({
          ...pagination,
          current: page,
          total: res.data.pagination.total
        });
      }
    } catch (error) {
      message.error('加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 搜索
  const handleSearch = () => {
    loadData(1);
  };

  // 重置
  const handleReset = () => {
    setFilters({ keyword: '', status: '', dateRange: null });
    setPagination({ ...pagination, current: 1 });
    setTimeout(() => loadData(1), 0);
  };

  // 查看详情
  const handleViewDetail = async (postId) => {
    setDetailVisible(true);
    setDetailLoading(true);
    try {
      const res = await getPostDetail(postId);
      if (res.success) {
        setCurrentPost(res.data);
      }
    } catch (error) {
      message.error('加载详情失败');
    } finally {
      setDetailLoading(false);
    }
  };

  // 删除帖子
  const handleDelete = async (postId) => {
    try {
      const res = await deletePost(postId);
      if (res.success) {
        message.success('删除成功');
        loadData(pagination.current);
      } else {
        message.error(res.message || '删除失败');
      }
    } catch (error) {
      message.error('删除失败');
    }
  };

  // 批量删除
  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要删除的帖子');
      return;
    }

    Modal.confirm({
      title: '批量删除确认',
      content: (
        <div>
          <p>确认删除选中的 <strong>{selectedRowKeys.length}</strong> 条帖子吗？</p>
          <p style={{ color: '#ff4d4f', fontSize: 12 }}>此操作不可恢复！</p>
        </div>
      ),
      okText: '确认删除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        setBatchDeleteLoading(true);
        try {
          const res = await batchDeletePosts({ ids: selectedRowKeys, reason: '批量删除' });
          if (res.success) {
            message.success(res.message || '批量删除成功');
            setSelectedRowKeys([]);
            loadData(pagination.current);
          } else {
            message.error(res.message || '批量删除失败');
          }
        } catch (error) {
          message.error('批量删除失败');
        } finally {
          setBatchDeleteLoading(false);
        }
      }
    });
  };

  // 状态标签
  const getStatusTag = (status) => {
    const statusMap = {
      pending: { color: 'warning', text: '待审核' },
      approved: { color: 'success', text: '已发布' },
      rejected: { color: 'error', text: '已拒绝' },
      deleted: { color: 'default', text: '已删除' }
    };
    const config = statusMap[status] || statusMap.approved;
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // 表格列配置
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 80
    },
    {
      title: '封面',
      dataIndex: 'cover_image',
      width: 100,
      render: (cover) => (
        cover ? (
          <Image
            src={cover}
            alt="cover"
            width={60}
            height={60}
            style={{ objectFit: 'cover', borderRadius: 4 }}
          />
        ) : (
          <div style={{ 
            width: 60, 
            height: 60, 
            background: '#f0f0f0', 
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            color: '#999'
          }}>
            无图片
          </div>
        )
      )
    },
    {
      title: '标题',
      dataIndex: 'title',
      width: 200,
      ellipsis: true
    },
    {
      title: '描述',
      dataIndex: 'description',
      width: 250,
      ellipsis: true,
      render: (text) => text || '-'
    },
    {
      title: '发布者',
      dataIndex: 'user_nickname',
      width: 120
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (status) => getStatusTag(status)
    },
    {
      title: '互动数据',
      key: 'interaction',
      width: 150,
      render: (_, record) => (
        <div style={{ fontSize: 12 }}>
          <div>👍 {record.like_count}</div>
          <div>⭐ {record.favorite_count}</div>
          <div>👁 {record.view_count}</div>
        </div>
      )
    },
    {
      title: '发布时间',
      dataIndex: 'created_at',
      width: 180,
      render: (time) => dayjs(time).format('YYYY-MM-DD HH:mm')
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button 
            type="link" 
            size="small" 
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record.id)}
          >
            查看
          </Button>
          {record.status !== 'deleted' && (
            <Popconfirm
              title="确认删除此帖子？"
              onConfirm={() => handleDelete(record.id)}
              okText="确认"
              cancelText="取消"
            >
              <Button 
                type="link" 
                size="small" 
                danger
                icon={<DeleteOutlined />}
              >
                删除
              </Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  // 批量选择配置
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
    getCheckboxProps: (record) => ({
      disabled: record.status === 'deleted',
    }),
  };

  return (
    <div style={{ padding: 24 }}>
      <h2>帖子管理</h2>

      {/* 筛选区 */}
      <div style={{ marginBottom: 16, background: '#fff', padding: 16, borderRadius: 8 }}>
        <Space wrap>
          <Input
            placeholder="搜索标题/描述/用户"
            value={filters.keyword}
            onChange={(e) => setFilters({ ...filters, keyword: e.target.value })}
            onPressEnter={handleSearch}
            style={{ width: 200 }}
            prefix={<SearchOutlined />}
          />
          
          <Select
            placeholder="帖子状态"
            value={filters.status}
            onChange={(value) => setFilters({ ...filters, status: value })}
            style={{ width: 120 }}
            allowClear
          >
            <Option value="pending">待审核</Option>
            <Option value="approved">已发布</Option>
            <Option value="rejected">已拒绝</Option>
            <Option value="deleted">已删除</Option>
          </Select>

          <RangePicker
            value={filters.dateRange}
            onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
            placeholder={['开始日期', '结束日期']}
          />

          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            搜索
          </Button>
          <Button onClick={handleReset}>重置</Button>
        </Space>
      </div>

      {/* 批量操作栏 */}
      {selectedRowKeys.length > 0 && (
        <div style={{ 
          marginBottom: 16, 
          background: '#e6f7ff', 
          padding: 12, 
          borderRadius: 4,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>已选择 <strong>{selectedRowKeys.length}</strong> 条</span>
          <Space>
            <Button onClick={() => setSelectedRowKeys([])}>取消选择</Button>
            <Button 
              type="primary" 
              danger
              icon={<DeleteOutlined />}
              loading={batchDeleteLoading}
              onClick={handleBatchDelete}
            >
              批量删除
            </Button>
          </Space>
        </div>
      )}

      {/* 帖子列表 */}
      <Table
        columns={columns}
        dataSource={posts}
        rowKey="id"
        loading={loading}
        rowSelection={rowSelection}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条`
        }}
        onChange={(newPagination) => loadData(newPagination.current)}
        scroll={{ x: 1400 }}
      />

      {/* 帖子详情弹窗 */}
      <Modal
        title="帖子详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={800}
      >
        {detailLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>加载中...</div>
        ) : currentPost ? (
          <div>
            {/* 基本信息 */}
            <div style={{ marginBottom: 24 }}>
              <h3>基本信息</h3>
              <p><strong>标题：</strong>{currentPost.title}</p>
              <p><strong>描述：</strong>{currentPost.description || '无'}</p>
              <p><strong>状态：</strong>{getStatusTag(currentPost.status)}</p>
              {currentPost.reject_reason && (
                <p><strong>拒绝原因：</strong>{currentPost.reject_reason}</p>
              )}
              <p><strong>地址：</strong>{currentPost.address || '无'}</p>
              <p><strong>坐标：</strong>{currentPost.latitude}, {currentPost.longitude}</p>
              <p><strong>是否共享：</strong>{currentPost.is_shared ? '是' : '否'}</p>
              <p><strong>发布时间：</strong>{dayjs(currentPost.created_at).format('YYYY-MM-DD HH:mm:ss')}</p>
            </div>

            {/* 用户信息 */}
            <div style={{ marginBottom: 24 }}>
              <h3>发布者信息</h3>
              <p><strong>昵称：</strong>{currentPost.user_nickname}</p>
              <p><strong>用户ID：</strong>{currentPost.user_id}</p>
              <p><strong>手机号：</strong>{currentPost.user_phone || '未绑定'}</p>
            </div>

            {/* 图片 */}
            {currentPost.photos && currentPost.photos.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <h3>图片</h3>
                <Image.PreviewGroup>
                  <Space wrap>
                    {currentPost.photos.map((photo) => (
                      <Image
                        key={photo.photo_url}
                        src={photo.photo_url}
                        width={100}
                        height={100}
                        style={{ objectFit: 'cover', borderRadius: 4 }}
                      />
                    ))}
                  </Space>
                </Image.PreviewGroup>
              </div>
            )}

            {/* 互动数据 */}
            <div style={{ marginBottom: 24 }}>
              <h3>互动数据</h3>
              <p><strong>点赞数：</strong>{currentPost.like_count}</p>
              <p><strong>收藏数：</strong>{currentPost.favorite_count}</p>
              <p><strong>分享数：</strong>{currentPost.share_count}</p>
              <p><strong>浏览数：</strong>{currentPost.view_count}</p>
            </div>

            {/* 审核记录 */}
            {currentPost.audits && currentPost.audits.length > 0 && (
              <div>
                <h3>审核记录</h3>
                <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                  {currentPost.audits.map((audit) => (
                    <div key={audit.id} style={{ 
                      padding: 12, 
                      border: '1px solid #f0f0f0',
                      borderRadius: 4,
                      marginBottom: 8
                    }}>
                      <div><strong>操作：</strong>{audit.action}</div>
                      {audit.reason && <div><strong>原因：</strong>{audit.reason}</div>}
                      <div style={{ fontSize: 12, color: '#666' }}>
                        操作人：{audit.admin_nickname || audit.admin_username} · 
                        时间：{dayjs(audit.created_at).format('YYYY-MM-DD HH:mm')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

export default Posts;
